import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, FileText, Paperclip, X, Plus } from "lucide-react";

const GMAIL_ACCOUNTS = [
  "info@alpinearmour.com.au",
  "scott@scottlistondesign.com.au"
];

function getDefaultEmailBody(jobName, clientName, isQuantityBreak) {
  if (isQuantityBreak) {
    return `Hi${clientName ? ` ${clientName}` : ""},

Please find attached your quantity break quote for ${jobName || "your order"}, showing pricing across the different quantities.

Let me know which quantity you'd like to go ahead with and I'll get everything finalised for you.

Looking forward to hearing from you.

Best regards`;
  }
  return `Hi${clientName ? ` ${clientName}` : ""},

Please find attached your quote for ${jobName || "your order"}.

If you have any questions or would like to proceed, please let me know.

Looking forward to hearing from you.

Best regards`;
}

export default function SendQuoteEmailModal({ 
  open, 
  onClose, 
  accounts, 
  jobName,
  jobNumber,
  customerEmail, 
  customerName,
  isQuantityBreak = false,
  onSend 
}) {
  const [to, setTo] = useState(customerEmail || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [from, setFrom] = useState(GMAIL_ACCOUNTS[0]);
  const [sending, setSending] = useState(false);
  const [additionalAttachments, setAdditionalAttachments] = useState([]);
  
  useEffect(() => {
    if (open) {
      setTo(customerEmail || "");
      setSubject(
        isQuantityBreak
          ? `Your Quantity Break Quote – ${jobName || jobNumber || "Quote"}`
          : `Your Quote – ${jobName || jobNumber || "Quote"}`
      );
      setBody(getDefaultEmailBody(jobName, customerName, isQuantityBreak));
      setAdditionalAttachments([]);
      
      // Set default sender to first connected account
      const connected = accounts?.find(a => a.connected);
      if (connected) setFrom(connected.email);
    }
  }, [open, customerEmail, customerName, jobName, jobNumber, accounts, isQuantityBreak]);
  
  // Handle file selection for additional attachments
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setAdditionalAttachments(prev => [...prev, ...files]);
    e.target.value = ""; // Reset for re-selection
  };
  
  // Remove additional attachment
  const removeAttachment = (index) => {
    setAdditionalAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleSend = async () => {
    if (!to.trim()) {
      return;
    }
    
    setSending(true);
    try {
      const success = await onSend({
        to,
        subject,
        body,
        from,
        additionalAttachments, // Pass additional files to parent
      });
      
      if (success) {
        onClose();
      }
    } finally {
      setSending(false);
    }
  };
  
  const connectedAccounts = accounts?.filter(a => a.connected) || [];
  const hasConnectedAccount = connectedAccounts.length > 0;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-aa-primary" />
            Send Quote
          </DialogTitle>
          <DialogDescription>
            Send the quote as a PDF attachment to the customer.
          </DialogDescription>
        </DialogHeader>
        
        {!hasConnectedAccount ? (
          <div className="py-6 text-center text-gray-500">
            <p>No Gmail accounts connected.</p>
            <p className="text-sm mt-2">Please connect a Gmail account in the Inbox settings first.</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* From */}
            <div className="flex items-center gap-3">
              <label className="w-20 text-sm text-gray-500 shrink-0">From:</label>
              <Select value={from} onValueChange={setFrom}>
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {connectedAccounts.map(acc => (
                    <SelectItem key={acc.email} value={acc.email}>{acc.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* To */}
            <div className="flex items-center gap-3">
              <label className="w-20 text-sm text-gray-500 shrink-0">To:</label>
              <input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
                placeholder="customer@example.com"
              />
            </div>
            
            {/* Subject */}
            <div className="flex items-center gap-3">
              <label className="w-20 text-sm text-gray-500 shrink-0">Subject:</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30"
              />
            </div>
            
            {/* Body */}
            <div className="flex gap-3">
              <label className="w-20 text-sm text-gray-500 shrink-0 pt-2">Message:</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={8}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-aa-primary/30 resize-none"
              />
            </div>
            
            {/* Attachments Section */}
            <div className="space-y-2">
              {/* Quote PDF (always attached) */}
              <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-lg">
                <Paperclip className="w-4 h-4 text-gray-500" />
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-red-500" />
                  <span className="text-sm text-gray-700">
                    {isQuantityBreak
                      ? `QuantityBreakQuote_${jobNumber || "PDF"}.pdf`
                      : `Quote_${jobNumber || "PDF"}.pdf`}
                  </span>
                </div>
                <span className="text-xs text-gray-400 ml-auto">
                  {isQuantityBreak
                    ? "Quantity break comparison PDF will be attached"
                    : "PDF will be generated and attached"}
                </span>
              </div>
              
              {/* Additional Attachments */}
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="quote-email-attachments"
                  data-testid="quote-email-attachments-input"
                />
                <label
                  htmlFor="quote-email-attachments"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add More Files
                </label>
                {additionalAttachments.length > 0 && (
                  <span className="text-xs text-gray-500">
                    +{additionalAttachments.length} additional file{additionalAttachments.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              
              {/* List additional attachments */}
              {additionalAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 pl-1">
                  {additionalAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 rounded text-xs"
                    >
                      <Paperclip className="w-3 h-3 text-gray-400" />
                      <span className="truncate max-w-[150px]">{file.name}</span>
                      <span className="text-gray-400">({(file.size / 1024).toFixed(1)}KB)</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="ml-1 text-gray-400 hover:text-red-500"
                        data-testid={`remove-attachment-${index}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <p className="text-xs text-gray-400">
              Your signature will be added automatically based on the sending account.
              The job status will be updated to "Sent" after sending.
            </p>
          </div>
        )}
        
        <DialogFooter className="pb-6">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            onClick={handleSend} 
            disabled={sending || !hasConnectedAccount || !to.trim()} 
            className="bg-aa-primary"
            data-testid="send-quote-confirm-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sending ? "Sending..." : "Send Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
